import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';

export interface InlineEditTableColumn {
  field: string;
  header: string;
  required?: boolean;
  editable?: boolean;
  controlType?: ControlType;
  autocompleteOptions?: any[];
  optionLabel?: string;
  optionValue?: string;
  createOptionMethod?: (value: string) => Observable<any>;
  createErrorKey?: string;
}

@UntilDestroy()
@Component({
  selector: 'app-client-side-inline-edit-data-table',
  templateUrl: './client-side-inline-edit-data-table.component.html',
  styleUrl: './client-side-inline-edit-data-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientSideInlineEditDataTableComponent implements OnChanges {
  @Input() columns: InlineEditTableColumn[] = [];
  @Input() data: any[] = [];
  @Input() selectionEnabled: boolean = false;
  @Input() selectFieldName: string = 'id';
  @Input() selectedIds: Array<string | number> = [];
  @Output() dataChange: EventEmitter<any[]> = new EventEmitter<any[]>();
  @Output() validChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() selectedIdsChange: EventEmitter<Array<string | number>> = new EventEmitter<
    Array<string | number>
  >();
  public controlType = ControlType;

  public form: FormGroup = this.fb.group({
    rows: this.fb.array([]),
  });

  constructor(private readonly fb: FormBuilder) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['columns']) {
      this.rebuildRows();
      this.syncSelectedIds();
      return;
    }

    if (changes['data']) {
      const previousData = changes['data'].previousValue as any[] | undefined;
      const currentData = changes['data'].currentValue as any[] | undefined;
      const previousLength = Array.isArray(previousData) ? previousData.length : 0;
      const currentLength = Array.isArray(currentData) ? currentData.length : 0;

      if (this.rowsFormArray.length === 0 || previousLength !== currentLength) {
        this.rebuildRows();
      } else {
        this.syncRowsWithData();
      }
    }

    if (this.rowsFormArray.length !== this.data.length) {
      this.rebuildRows();
    }

    if (changes['selectedIds']) {
      this.syncSelectedIds();
      return;
    }

    if (changes['data']) {
      this.syncSelectedIds();
    }
  }

  public get rowsFormArray(): FormArray<FormGroup> {
    return this.form.get('rows') as FormArray<FormGroup>;
  }

  public onRowChange(rowIndex: number): void {
    const rowValue = this.rowsFormArray.at(rowIndex).value;
    const updatedData = this.data.map((item, index) => {
      if (index !== rowIndex) {
        return item;
      }

      return {
        ...item,
        ...rowValue,
      };
    });

    this.data = updatedData;
    this.dataChange.emit(updatedData);
    this.emitValidity();
  }

  public onToggleSelectAll(checked: boolean): void {
    if (!this.selectionEnabled) {
      return;
    }

    this.setSelectedIds(checked ? this.getAllRowIds() : [], true);
  }

  public onToggleRow(rowIndex: number, checked: boolean): void {
    if (!this.selectionEnabled) {
      return;
    }

    const rowId = this.getRowId(this.data[rowIndex], rowIndex);

    if (checked) {
      this.setSelectedIds([...this.selectedIds, rowId], true);
      return;
    }

    this.setSelectedIds(this.selectedIds.filter((id) => id !== rowId), true);
  }

  public isRowSelected(rowIndex: number): boolean {
    const rowId = this.getRowId(this.data[rowIndex], rowIndex);

    return this.selectedIds.includes(rowId);
  }

  public get areAllRowsSelected(): boolean {
    if (!this.selectionEnabled) {
      return false;
    }

    const rowIds = this.getAllRowIds();

    return rowIds.length > 0 && rowIds.every((id) => this.selectedIds.includes(id));
  }

  public get isSelectAllIndeterminate(): boolean {
    if (!this.selectionEnabled) {
      return false;
    }

    const rowIds = this.getAllRowIds();

    if (rowIds.length === 0) {
      return false;
    }

    const selectedRowsCount = rowIds.filter((id) => this.selectedIds.includes(id)).length;

    return selectedRowsCount > 0 && selectedRowsCount < rowIds.length;
  }

  private rebuildRows(): void {
    this.rowsFormArray.clear();

    this.data.forEach((item, rowIndex) => {
      const group: { [key: string]: FormControl } = {};

      this.columns.forEach((column) => {
        if (column.editable === false) {
          return;
        }

        const controlValue = (item[column.field] as string | number | null) ?? '';
        const validators = column.required ? [Validators.required] : [];

        group[column.field] = this.fb.control(controlValue, { validators });
      });

      const rowGroup = this.fb.group(group);

      rowGroup.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
        this.onRowChange(rowIndex);
      });

      this.rowsFormArray.push(rowGroup);
    });

    this.emitValidity();
  }

  private emitValidity(): void {
    this.validChange.emit(this.form.valid);
  }

  private syncRowsWithData(): void {
    this.data.forEach((item, rowIndex) => {
      const rowGroup = this.rowsFormArray.at(rowIndex);

      if (!rowGroup) {
        return;
      }

      this.columns.forEach((column) => {
        if (column.editable === false) {
          return;
        }

        const control = rowGroup.get(column.field);

        if (!control) {
          return;
        }

        const nextValue = (item?.[column.field] as string | number | null) ?? '';

        if (control.value !== nextValue) {
          control.setValue(nextValue, { emitEvent: false });
        }
      });
    });

    this.emitValidity();
  }

  private syncSelectedIds(): void {
    if (!this.selectionEnabled) {
      return;
    }

    this.setSelectedIds(this.selectedIds, false);
  }

  private setSelectedIds(ids: Array<string | number>, emit: boolean): void {
    const rowIds = new Set(this.getAllRowIds());
    const uniqueIds = [...new Set(ids)];
    const filteredIds = uniqueIds.filter((id) => rowIds.has(id));
    const hasChanged =
      filteredIds.length !== this.selectedIds.length ||
      filteredIds.some((id, index) => id !== this.selectedIds[index]);

    this.selectedIds = filteredIds;

    if (emit || hasChanged) {
      this.selectedIdsChange.emit([...this.selectedIds]);
    }
  }

  private getAllRowIds(): Array<string | number> {
    return this.data.map((item, index) => this.getRowId(item, index));
  }

  private getRowId(item: any, rowIndex: number): string | number {
    const rowId = item?.[this.selectFieldName];

    return rowId ?? rowIndex;
  }
}

export enum ControlType {
  Input = 'input',
  SingleSelectAutocomplete = 'single-select-autocomplete',
  SingleSelectAutocompleteWithCreate = 'single-select-autocomplete-with-create',
}
